import { Slide } from '@/components/slides/Slide';
import { Lesson } from '@/models/lesson';
import { parseSlide } from '@/types/slide';
import { useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Paper } from '@mui/material';
import { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { GET_SLIDES, GetSlidesResponse, GetSlidesVariables } from './gql';
import './print.css';

export const LessonPrintingDialog: FC<
    {
        lesson: Lesson;
        onCancel?: () => void;
    } & DialogProps
> = ({ lesson, onCancel, ...props }) => {
    const { t } = useTranslation();

    const { data } = useQuery<GetSlidesResponse, GetSlidesVariables>(GET_SLIDES, {
        variables: {
            where: {
                lessonVersion: {
                    id: {
                        equals: lesson.latestLessonDraft.id,
                    },
                },
            },
        },
    });

    const slides = useMemo(() => {
        return data ? data.slides.map((slide) => parseSlide(slide)) : [];
    }, [data]);

    // ui state
    const [printing, setPrinting] = useState(false);

    // printing ref
    const contentRef = useRef<HTMLDivElement>(null);
    const print = useReactToPrint({
        contentRef,
        documentTitle: lesson.latestLessonDraft.lessonName,
        onBeforePrint: async () => {
            setPrinting(true);
        },
        onAfterPrint: () => {
            setPrinting(false);
        },
    });

    return (
        <Dialog {...props}>
            <DialogTitle>{lesson.latestLessonDraft.lessonName}</DialogTitle>
            <DialogContent>
                <Box sx={{ py: 1 }}>
                    <Box ref={contentRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <style type='text/css' media='print'>
                            {`@page { size: landscape; margin: 0 }`}
                        </style>
                        {slides.map((slide) => (
                            <div key={slide.id} className='page-container'>
                                <Paper
                                    sx={{
                                        height: '768px',
                                        width: '1024px',
                                        borderRadius: 0,
                                        overflow: 'hidden',
                                    }}
                                    elevation={3}
                                >
                                    <Slide components={slide.data} preview />
                                </Paper>
                            </div>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        onCancel?.();
                    }}
                >
                    {t('common.cancel')}
                </Button>
                <Button loading={printing} variant='contained' onClick={() => print()} loadingIndicator={<CircularProgress size={16} />}>
                    {t('common.print')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
